import { createSlice } from "@reduxjs/toolkit";
import { register, login } from "../utils/requests/postRequests";
import { getMeData } from "utils/requests/getRequest";
import { interfaceActions } from "./interface";
import { setUpCart } from "./cartSlice";
import { redirectToDashboard, isMerchantUser } from "../utils/sso-helpers";

const initialState = {
  isAuthenticated: false,
  user: {},
  subscription: {},
  subscriptionStatus: "",
  validationErrors: {},
};

const userSlice = createSlice({
  name: "userStatus",
  initialState,
  reducers: {
    login(state) {
      state.isAuthenticated = true;
    },
    logout(state) {
      state.isAuthenticated = false;
      state.user = {};
      state.subscription = {};
      state.subscriptionStatus = "";
      state.validationErrors = {};
    },
    user(state, action) {
      state.user = action.payload;
    },
    subscription(state, action) {
      state.subscription = action.payload;
    },
    subscriptionStatus(state, action) {
      state.subscriptionStatus = action.payload;
    },
    setValidationErrors(state, action) {
      state.validationErrors = action.payload;
    },
    clearValidationErrors(state) {
      state.validationErrors = {};
    },
  },
});

export const loginUser = (
  credentials: {
    email: string | undefined;
    password: string | undefined;
  },
  history: any,
  moveTo: string = "/",
  cartData: any = {},
  setOpenLoginModal?: any,
  setOpenFlowCartModal?: any
): any => {
  return async (dispatch: any) => {
    let data = await login(credentials);
    data.signed_in = new Date();
    if (data?.user) {
      localStorage.setItem("user", JSON.stringify(data));
      const user = await getMeData(data.access_token);
      if (user?.subscriptions?.length && user?.subscriptions?.[0]?.id) {
        dispatch(userActions.subscription(user.subscriptions[0]));
        dispatch(
          userActions.subscriptionStatus(user?.user?.subscription_status)
        );
      }

      dispatch(setUpCart());
      dispatch(userActions.login());
      dispatch(userActions.user({ user: data?.user }));
      dispatch(userActions.clearValidationErrors());
      dispatch(
        interfaceActions.setSnackBar({
          show: true,
          status: "success",
          msg: "loginSuccess",
        })
      );

      // Check if user is a merchant and redirect to dashboard
      if (isMerchantUser(data?.user)) {
        // Merchant user - redirect to dashboard
        redirectToDashboard(data.access_token, data.user, "/");
        return; // Stop here, don't continue with normal flow
      }

      // Normal user flow (not a merchant)
      if (moveTo !== "noNavigate" && moveTo !== "payment-page") {
        history(moveTo, { state: cartData });
      } else if (moveTo === "payment-page") {
        history(`/${moveTo}`);
      } else {
        setOpenLoginModal(false);
        setOpenFlowCartModal(true);
      }
    } else {
      dispatch(
        interfaceActions.setSnackBar({
          show: true,
          status: "error",
          msg: "loginError",
        })
      );
    }
  };
};

export const registerUser = (
  user: {
    first_name: string | undefined;
    last_name: string | undefined;
    email: string | undefined;
    password: string | undefined;
    phone_number: string | undefined;
  },
  history: any,
  setOpenLoginModal?: any,
  setOpenFlowCartModal?: any,
  moveTo?: string
): any => {
  return async (dispatch: any) => {
    dispatch(userActions.clearValidationErrors());
    
    const data = await register(user);

    if (data?.user) {
      localStorage.setItem("access_token", data?.access_token);
      localStorage.setItem("expires_in", data?.expires_in);
      localStorage.setItem("last_login", JSON.stringify(new Date()));
      dispatch(
        interfaceActions.setSnackBar({
          show: true,
          status: "success",
          msg: "Επιτυχής εγγραφή",
        })
      );
      let dataLogin = await login({
        email: user?.email,
        password: user?.password,
      });
      if (dataLogin?.user) {
        dispatch(userActions.login());
        dispatch(userActions.user({ user: dataLogin?.user }));
        dataLogin.signed_in = new Date();
        localStorage.setItem("user", JSON.stringify(dataLogin));
        if (moveTo) {
          history(`/${moveTo}`);
        } else if (!moveTo && setOpenLoginModal) {
          setOpenLoginModal(false);
          setOpenFlowCartModal(true);
        } else {
          history("/");
        }
      }
    } else {
      // Initialize formatted errors object for Formik fields
      let formattedErrors = {};
      let mainErrorMessage = data?.message || '';
      
      // Process errors from the server response
      if (data?.errors && typeof data.errors === 'object') {
        const fieldMapping: Record<string, string> = {
          first_name: 'firstName',
          last_name: 'lastName',
          phone_number: 'phoneNumber',
          email: 'email',
          password: 'password'
        };
        
        // Process each field with validation errors
        Object.entries(data.errors).forEach(([field, fieldErrors]) => {
          if (typeof fieldErrors === 'object' && fieldErrors !== null) {
            const formikField = fieldMapping[field] || field;
            
            // Get the first error message for this field
            const firstErrorKey = Object.keys(fieldErrors as object)[0];
            const errorMessage = (fieldErrors as Record<string, string>)[firstErrorKey];
            
            // Store the error for the specific field
            formattedErrors = {
              ...formattedErrors,
              [formikField]: errorMessage
            };
            
            // Set the main error for the snackbar if not already set
            if (!mainErrorMessage || mainErrorMessage === "Invalid body, check 'errors' property for more info.") {
              mainErrorMessage = errorMessage;
            }
          }
        });
      }
      
      // Store validation errors in Redux for form fields to display
      dispatch(userActions.setValidationErrors(formattedErrors));
      
      // Show the main error message in the snackbar
      dispatch(
        interfaceActions.setSnackBar({
          show: true,
          status: "error",
          msg: mainErrorMessage || "Σφάλμα κατά την εγγραφή",
        })
      );
    }
  };
};

export const getValidationErrors = (state: any) => state.user?.validationErrors || {};

export const logoutUser = (): any => {
  return (dispatch: any) => {
    localStorage.removeItem("user");
    localStorage.removeItem("access_token");
    localStorage.removeItem("expires_in");
    localStorage.removeItem("last_login");
    dispatch(userActions.logout());
    dispatch(
      interfaceActions.setSnackBar({
        show: false,
        status: "success",
        msg: "Successfully logged out",
      })
    );
  };
};

export const userActions = userSlice.actions;
export default userSlice.reducer;
